import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";
import { ArrowLeft, Calendar, Loader2 } from "lucide-react";
import { getBlogPost } from "../api";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");
const FALLBACK_IMG = "https://images.unsplash.com/photo-1706606992658-b7225196f1af";

const BlogDetail = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    getBlogPost(slug).then(setData).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#8a8a8a] gap-2"><Loader2 size={18} className="animate-spin" /> Loading…</div>;
  if (notFound || !data) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center">
      <h1 className="font-display text-[30px] text-[#2b2b2b] mb-3">Post not found</h1>
      <Link to="/blog" className="text-[#b8935f] hover:underline text-[14px]">← Back to the blog</Link>
    </div>
  );

  const p = data.post;
  return (
    <div className="bg-white" data-testid="blog-detail">
      <Seo title={`${p.title} — Anadear Estates Blog`} description={p.excerpt} path={`/blog/${p.slug}`} />
      <div className="relative h-[360px] flex items-end">
        <div className="absolute inset-0">
          <img src={p.coverImage || FALLBACK_IMG} alt={p.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
        </div>
        <div className="relative z-10 max-w-[820px] mx-auto px-5 md:px-8 pb-10 w-full">
          <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[12px] tracking-[0.1em] uppercase text-white border border-white/20">{p.category}</span>
          <h1 className="font-display text-white text-[32px] md:text-[46px] font-300 leading-tight mt-4">{p.title}</h1>
          <p className="text-white/70 text-[13px] mt-3 flex items-center gap-2"><Calendar size={14} /> {fmt(p.publishedAt)}{p.author ? ` · ${p.author}` : ""}</p>
        </div>
      </div>

      <article className="max-w-[820px] mx-auto px-5 md:px-8 py-14">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13px] text-[#8a8a8a] hover:text-[#b8935f] mb-8"><ArrowLeft size={15} /> All articles</Link>
        <div className="text-[16px] text-[#3a3a3a] leading-[1.8] whitespace-pre-wrap">{p.body}</div>
      </article>

      {data.related?.length > 0 && (
        <section className="border-t border-[#f0ece5] py-14 bg-[#faf7f2]">
          <div className="max-w-[1100px] mx-auto px-5 md:px-8">
            <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-6">More from the blog</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {data.related.map((r) => (
                <Link to={`/blog/${r.slug}`} key={r.id} className="group bg-white rounded-xl border border-[#f0ece5] overflow-hidden">
                  <img src={r.coverImage || FALLBACK_IMG} alt={r.title} className="w-full h-40 object-cover" />
                  <div className="p-4">
                    <p className="text-[11px] uppercase tracking-wide text-[#b8935f] mb-1">{r.category}</p>
                    <h3 className="font-display text-[17px] font-500 text-[#2b2b2b] group-hover:text-[#b8935f] transition-colors leading-snug">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetail;
